import React, {FunctionComponent, useMemo, useRef} from "react";
import {Player} from "@lottiefiles/react-lottie-player";
import classnames from "classnames";
import {AnimationItem} from "lottie-web";
import BizkeytechLoadingAnimationSrc from "../../../assets/animations/lottie/bizkeytech-loading.json";
import CircularLoadingAnimationSrc from "../../../assets/animations/lottie/circular-loading.json";
import {EnvService, Utils} from "../../../core";
import {useTheme} from "@mui/material";


export interface LottieAnimationProps {
    className?: string,
    width?: string | number,
    height?: string | number,
    src: string | object,
}

const LottieAnimation: FunctionComponent<LottieAnimationProps> = ({
                                                                      className,
                                                                      width = '100%',
                                                                      height = 'auto',
                                                                      src
                                                                  }) => {
    const playerRef = useRef<Player>(null);
    const lottieRef = useRef<AnimationItem>();
    const theme = useTheme();

    const coloredSrc = useMemo(() => {
        const utils = new LottieAnimationUtils(theme.palette.primary.main, theme.palette.secondary.main);
        if (src === BizkeytechLoadingAnimation)
            return utils.convertBizkeyTechLoadingColors(src);
        if (src === CircularLoadingAnimation)
            return utils.convertCircularLoadingColors(src);
        return src;
    }, [theme.palette?.primary?.main, theme.palette?.secondary?.main, src]);

    /**
     * Fetches the value of the given dimension.
     *
     * * if the given [d] is a number, returns the pixelated value of the [d], otherwise returns [d]
     */
    const getDimensionValue = (d: string | number): string | any => {
        const num = Number(d);
        if (Number.isNaN(num)) {
            return d;
        }
        return `${num}px`;
    }

    const dimensions = useMemo(() => ({
        width: getDimensionValue(width),
        height: getDimensionValue(height),
    }), [width, height])

    return (
        <>
            <Player
                autoplay
                loop
                src={coloredSrc}
                ref={playerRef}
                style={{...dimensions}}
                lottieRef={(ref) => (lottieRef.current = ref)}
                className={classnames(className)}
            />
        </>
    )
}

export default LottieAnimation;


/**
 * Utility class for lottie animations.
 */
class LottieAnimationUtils {

    private primaryColor?: number[];
    private secondaryColor?: number[];
    private middleColor?: number[];

    /**
     * Creates a new instance of the LottieAnimationUtils.
     * @param primaryColor      The primary color.
     * @param secondaryColor    The secondary color.
     */
    constructor(primaryColor?: string, secondaryColor?: string) {
        primaryColor = primaryColor ?? EnvService.primaryColor;
        secondaryColor = secondaryColor ?? EnvService.secondaryColor;
        if (primaryColor.startsWith('#')) {
            this.primaryColor = this.hexToNormalizedRgb(primaryColor);
        } else {
            this.primaryColor = this.rgbToNormalizedRgba(primaryColor);
        }
        if (secondaryColor.startsWith('#')) {
            this.secondaryColor = this.hexToNormalizedRgb(secondaryColor);
        } else {
            this.secondaryColor = this.rgbToNormalizedRgba(secondaryColor);
        }
        this.middleColor = this.interpolateColor(this.primaryColor, this.secondaryColor);
    }

    /**
     * Interpolates the given colors.
     * @param color1        The first color.
     * @param color2        The second
     * @returns  The interpolated color.
     */
    private interpolateColor(color1: number[], color2: number[]): number[] {
        const r = (color1[0] + color2[0]) / 2;
        const g = (color1[1] + color2[1]) / 2;
        const b = (color1[2] + color2[2]) / 2;
        return [r, g, b, 1];
    }

    /**
     * Converts the given hex color to a normalized RGBA color.
     * @param hex   The hex color to convert.
     * @returns     The normalized RGBA color.
     */
    private hexToNormalizedRgb(hex: string): number[] {
        const bigint = parseInt(hex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return [r / 255, g / 255, b / 255, 1]; // 1 for alpha
    }

    private rgbToNormalizedRgba(rgb: string): number[] {
        const rgbArray = rgb.split(',');
        const r = parseInt(rgbArray[0].slice(4));
        const g = parseInt(rgbArray[1]);
        const b = parseInt(rgbArray[2].slice(0, -1));
        return [r / 255, g / 255, b / 255, 1]; // 1 for alpha
    }


    /**
     * Returns the normalized primary color.
     */
    get normalizedPrimaryColor(): number[] {
        if (this.primaryColor) {
            return this.primaryColor;
        }
        this.primaryColor = this.hexToNormalizedRgb(EnvService.primaryColor);
        return this.primaryColor;
    }

    /**
     * Returns the normalized secondary color.
     */
    get normalizedSecondaryColor(): number[] {
        if (this.secondaryColor) {
            return this.secondaryColor;
        }
        this.secondaryColor = this.hexToNormalizedRgb(EnvService.secondaryColor);
        return this.secondaryColor;
    }

    /**
     * Returns the normalized middle color.
     */
    get normalizedMiddleColor() {
        if (this.middleColor) {
            return this.middleColor;
        }
        this.middleColor = this.interpolateColor(this.normalizedPrimaryColor, this.normalizedSecondaryColor);
        return this.middleColor;
    }

    /**
     * Converts the color of the given json to the primary color.
     * @param json  The json to convert.
     * @returns     The converted json.
     */
    public convertBizkeyTechLoadingColors(json: object): object {
        const result = Utils.deepCopy<any>(json);
        result.layers.forEach((layer: any) => {
            if (layer.shapes) {
                layer.shapes.forEach((shape: any) => {
                    if (shape.it) {
                        shape.it.forEach((item: any) => {
                            if (item.ty === 'fl' && item.c) {
                                item.c.k = this.normalizedPrimaryColor;
                            }
                        });
                    }
                });
            }
        });
        return result;
    }

    /**
     * Converts the color of the given json to the primary color.
     * @param json  The json to convert.
     * @returns     The converted json.
     */
    public convertCircularLoadingColors(json: object): object {
        const result = Utils.deepCopy<any>(json);
        result.layers.forEach((layer: any) => {
            if (layer.shapes) {
                layer.shapes.forEach((shape: any) => {
                    if (shape.it) {
                        shape.it.forEach((item: any) => {
                            if (item.ty === 'gs') { // Check for gradient stroke type
                                const stops = item.g.k.k;
                                // Update the gradient stops
                                stops[1] = this.normalizedPrimaryColor[0];
                                stops[2] = this.normalizedPrimaryColor[1];
                                stops[3] = this.normalizedPrimaryColor[2];
                                stops[5] = this.normalizedMiddleColor[0];
                                stops[6] = this.normalizedMiddleColor[1];
                                stops[7] = this.normalizedMiddleColor[2];
                                stops[9] = this.normalizedSecondaryColor[0];
                                stops[10] = this.normalizedSecondaryColor[1];
                                stops[11] = this.normalizedSecondaryColor[2];
                            }
                        });
                    }
                });
            }
        });
        return result;
    }
}

export const BizkeytechLoadingAnimation = new LottieAnimationUtils().convertBizkeyTechLoadingColors(BizkeytechLoadingAnimationSrc)
export const CircularLoadingAnimation = new LottieAnimationUtils().convertCircularLoadingColors(CircularLoadingAnimationSrc)

